import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faEye, faSearch, faTimes, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../common/ConfirmModal';
import { formatMoney } from '../shared/formatMoney';
const { rootUrl } = require('../../../config/config');

const VendorManagement = () => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken');
    const navigate = useNavigate();

    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Portal dropdown: store the active vendor + fixed coords
    const [dropVendor, setDropVendor] = useState(null);
    const [dropPos, setDropPos] = useState({ top: 0, right: 0 });

    const [confirmDelete, setConfirmDelete] = useState({
        open: false, vendorId: null, vendorName: '', deleting: false, error: ''
    });

    // Close dropdown on outside click
    useEffect(() => {
        if (!dropVendor) return;
        const handler = (e) => {
            if (!e.target.closest('[data-dd]')) setDropVendor(null);
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [dropVendor]);

    const openDrop = (e, vendor) => {
        e.stopPropagation();
        if (dropVendor?.id === vendor.id) {
            setDropVendor(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        setDropPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        setDropVendor(vendor);
    };

    const fetchVendors = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${rootUrl}/parts/suppliers`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (res.data) {
                const list = res.data;
                const withBalances = await Promise.all(list.map(async v => {
                    try {
                        const r = await axios.get(`${rootUrl}/api/vendors/${v.id}/ledger`, {
                            headers: { Authorization: `Bearer ${authToken}` }
                        });
                        return { ...v, balance: r.data?.balance ?? null };
                    } catch {
                        return { ...v, balance: null };
                    }
                }));
                setVendors(withBalances);
            }
        } catch (err) {
            console.error('Error fetching vendors:', err);
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        fetchVendors();
    }, [fetchVendors]);

    const handleDeleteConfirm = async () => {
        setConfirmDelete(p => ({ ...p, deleting: true, error: '' }));
        try {
            await axios.delete(`${rootUrl}/parts/suppliers/${confirmDelete.vendorId}`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            setConfirmDelete({ open: false, vendorId: null, vendorName: '', deleting: false, error: '' });
            fetchVendors();
        } catch (err) {
            setConfirmDelete(p => ({
                ...p,
                deleting: false,
                error: err.response?.data?.error || 'Error deleting vendor'
            }));
        }
    };

    const openDelete = (vendor) => {
        setDropVendor(null);
        setConfirmDelete({ open: true, vendorId: vendor.id, vendorName: vendor.name, deleting: false, error: '' });
    };

    const filteredVendors = vendors.filter(v =>
        v.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // The single portal dropdown rendered into document.body
    const DropdownPortal = dropVendor ? createPortal(
        <div
            data-dd
            style={{ position: 'fixed', top: dropPos.top, right: dropPos.right, zIndex: 9999 }}
            className="bg-white border border-gray-200 rounded-sm shadow-xl py-1 min-w-[148px]"
        >
            <button
                data-dd
                onClick={() => { setDropVendor(null); navigate(`/vendors/${dropVendor.id}/ledger`); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 text-left"
            >
                <FontAwesomeIcon icon={faEye} className="text-gray-400 w-3 flex-shrink-0" />
                View Ledger
            </button>
            <button
                data-dd
                onClick={() => { setDropVendor(null); navigate(`/vendors/${dropVendor.id}/edit`); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 text-left"
            >
                <FontAwesomeIcon icon={faEdit} className="text-gray-400 w-3 flex-shrink-0" />
                Edit
            </button>
            <div className="border-t border-gray-100 my-0.5" />
            <button
                data-dd
                onClick={() => openDelete(dropVendor)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-600 hover:bg-red-50 text-left"
            >
                <FontAwesomeIcon icon={faTrash} className="w-3 flex-shrink-0" />
                Delete
            </button>
        </div>,
        document.body
    ) : null;

    const MenuBtn = ({ vendor }) => (
        <button
            data-dd
            onClick={(e) => openDrop(e, vendor)}
            className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-200 hover:bg-gray-100 text-gray-500 transition-colors"
        >
            <FontAwesomeIcon icon={faEllipsisV} className="text-xs" />
        </button>
    );

    return (
        <div className="p-4 md:p-6 min-h-screen bg-[#fff8f5]">
            {/* Page header */}
            <div className="flex justify-between items-start mb-5">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF5733]">Vendors</p>
                    <h1 className="text-xl font-bold text-gray-900">Vendor Management</h1>
                </div>
                <button
                    onClick={() => navigate('/vendors/new')}
                    className="flex items-center gap-2 px-3 py-2 bg-[#FF5733] hover:bg-[#ff4520] text-white text-xs font-semibold rounded-sm transition-colors"
                >
                    <FontAwesomeIcon icon={faPlus} />
                    <span className="hidden sm:inline">Add Vendor</span>
                </button>
            </div>

            {/* Single card — search + table */}
            <div className="bg-white rounded-2xl shadow-xl">
                {/* Search */}
                <div className="px-4 py-3 border-b border-gray-100">
                    <div className="relative max-w-xs">
                        <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Search by name, code or contact..."
                            className="w-full pl-8 pr-7 py-1.5 border border-gray-200 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="p-10 text-center text-xs text-gray-400">Loading...</div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full table-fixed text-xs min-w-[760px]">
                                <colgroup>
                                    <col className="w-20" />
                                    <col className="w-44" />
                                    <col className="w-32" />
                                    <col className="w-28" />
                                    <col className="w-24" />
                                    <col className="w-32" />
                                    <col className="w-28" />
                                    <col className="w-14" />
                                </colgroup>
                                <thead>
                                    <tr className="border-b border-gray-100 bg-gray-50/60">
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Code</th>
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Name</th>
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Contact</th>
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Phone</th>
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Terms</th>
                                        <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Balance</th>
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                                        <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {filteredVendors.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-10 text-center text-xs text-gray-400">
                                                {searchTerm ? 'No vendors match your search' : 'No vendors found'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredVendors.map(vendor => (
                                            <tr key={vendor.id} className="hover:bg-blue-gray-50/30 transition-colors">
                                                <td className="px-4 py-2.5 font-mono text-gray-500">{vendor.code}</td>
                                                <td className="px-4 py-2.5 font-medium text-gray-800 truncate">{vendor.name}</td>
                                                <td className="px-4 py-2.5 text-gray-500 truncate">{vendor.contact_person || '-'}</td>
                                                <td className="px-4 py-2.5 text-gray-500">{vendor.phone || '-'}</td>
                                                <td className="px-4 py-2.5 text-gray-500">{vendor.payment_terms || '-'}</td>
                                                <td className="px-4 py-2.5 text-right font-semibold tabular-nums">
                                                    {vendor.balance === null || vendor.balance === undefined
                                                        ? <span className="text-gray-300 font-normal">-</span>
                                                        : <span className={vendor.balance > 0 ? 'text-red-600' : 'text-gray-800'}>
                                                            {formatMoney(vendor.balance)}
                                                        </span>
                                                    }
                                                </td>
                                                <td className="px-4 py-2.5">
                                                    <div className="flex flex-wrap gap-1">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${vendor.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                                            {vendor.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                        {vendor.is_preferred && (
                                                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">Preferred</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2.5">
                                                    <div className="flex justify-end">
                                                        <MenuBtn vendor={vendor} />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile rows */}
                        <div className="md:hidden divide-y divide-gray-100">
                            {filteredVendors.length === 0 ? (
                                <div className="p-10 text-center text-xs text-gray-400">
                                    {searchTerm ? 'No vendors match your search' : 'No vendors found'}
                                </div>
                            ) : (
                                filteredVendors.map(vendor => (
                                    <div key={vendor.id} className="px-4 py-3">
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="min-w-0">
                                                <div className="font-semibold text-sm text-gray-800 truncate">{vendor.name}</div>
                                                <div className="text-[10px] text-gray-400 font-mono mt-0.5">{vendor.code}</div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                {vendor.balance !== null && vendor.balance !== undefined && (
                                                    <span className={`text-xs font-semibold tabular-nums ${vendor.balance > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                                                        {formatMoney(vendor.balance)}
                                                    </span>
                                                )}
                                                <MenuBtn vendor={vendor} />
                                            </div>
                                        </div>
                                        <div className="flex gap-1.5 mt-1.5 flex-wrap">
                                            <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${vendor.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                                {vendor.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                            {vendor.is_preferred && (
                                                <span className="text-[10px] px-2 py-0.5 rounded font-medium bg-blue-50 text-blue-700">Preferred</span>
                                            )}
                                        </div>
                                        {(vendor.contact_person || vendor.phone) && (
                                            <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                                                {vendor.contact_person && <div>{vendor.contact_person}</div>}
                                                {vendor.phone && <div>{vendor.phone}</div>}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>

            {DropdownPortal}

            <ConfirmModal
                isOpen={confirmDelete.open}
                title="Delete Vendor"
                description={`Are you sure you want to delete "${confirmDelete.vendorName}"? This action cannot be undone.`}
                onCancel={() => setConfirmDelete({ open: false, vendorId: null, vendorName: '', deleting: false, error: '' })}
                onConfirm={handleDeleteConfirm}
                confirmText="Delete"
                loading={confirmDelete.deleting}
                error={confirmDelete.error}
                variant="danger"
            />
        </div>
    );
};

export default VendorManagement;
